import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Layers, Activity, Gavel, HeartPulse } from 'lucide-react';

import Sidebar from '../layouts/Sidebar';
import KpiCard from '../components/ui/KpiCard';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import { PageLoader } from '../components/ui/Spinner';
import CommandCenterService from '../services/CommandCenterService';

const STAGE_STYLES = {
  done: { badge: 'bg-green-100 text-green-700' },
  active: { badge: 'bg-blue-100 text-blue-700' },
  watch: { badge: 'bg-orange-100 text-orange-700' },
};

const STAGE_NUMBERS = { activate: '1', assign: '2', respond: '3', document: '4', report: '5' };

// Decon lane throughput, risk score and stakeholder-ack % have no backing model yet
// (no decon-lane or stakeholder-ack table exists). Tracked here as a manual, session-local
// estimate the command team can nudge, rather than faking a live backend computation.
const INITIAL_LANE_METRICS = {
  deconPending: 5,
  deconThroughput: 72,
  riskScore: 68,
  stakeholderAck: 72,
};

export default function CommandCenter() {
  const [stats, setStats] = useState(null);
  const [laneMetrics, setLaneMetrics] = useState(INITIAL_LANE_METRICS);
  const [modal, setModal] = useState(null); // 'sitrep' | 'conference' | null
  const [loading, setLoading] = useState(true);

  const loadStats = async () => {
    try {
      const response = await CommandCenterService.getStats();
      if (response?.status === 200) {
        setStats(response.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    loadStats().finally(() => setLoading(false));
    const intervalId = setInterval(loadStats, 30000);
    return () => clearInterval(intervalId);
  }, []);

  const openLane = () => {
    setLaneMetrics((m) => ({
      ...m,
      deconPending: Math.max(0, m.deconPending - 2),
      deconThroughput: Math.min(100, m.deconThroughput + 10),
      riskScore: Math.max(0, m.riskScore - 8),
    }));
    toast.success('Standby decon lane opened (manual estimate updated).');
  };

  const incident = stats?.activeIncident || null;
  const workflowStages = stats?.workflowStages || [];
  const alerts = stats?.alerts || [];

  const commandKpis = [
    { label: 'Disaster Scale', value: `Level ${stats?.disasterLevel ?? 1}`, note: 'Facility + external stakeholder coordination', icon: Layers, type: 'amber' },
    { label: 'Operational Tempo', value: stats?.operationalTempo ?? 'Idle', note: 'Patient + decon flow active', icon: Activity, type: 'red' },
    { label: 'Decision Required', value: stats?.openDecisions ?? 0, note: 'Lane capacity and stakeholder ack', icon: Gavel, type: 'amber' },
    { label: 'Clinical Risk', value: incident?.casualties?.high ?? 0, note: 'Red patient visibility required', icon: HeartPulse, type: 'red' },
  ];

  const sitrepDefault = incident
    ? `Active CBRNE response: ${incident.title} (${incident.type}) at ${incident.location || 'location pending'}. ` +
      `${incident.teamsAssigned} team(s) assigned, ${stats.openDecisions} response(s) pending, ` +
      `casualties logged: ${incident.casualties.high} high / ${incident.casualties.medium} medium / ${incident.casualties.low} low. ` +
      `Decon pending: ${laneMetrics.deconPending} (manual estimate), stakeholder acknowledgement: ${laneMetrics.stakeholderAck}% (manual estimate).`
    : 'No active CBRNE incident to report.';

  if (loading) {
    return (
      <Sidebar title="Command Center">
        <PageLoader label="Loading command center…" />
      </Sidebar>
    );
  }

  return (
    <Sidebar title="Command Center">
      <section className="space-y-6">
        <div className="bg-seha-500 text-white rounded-xl p-6 shadow-panel relative overflow-hidden">
          <div className="relative z-10 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${incident ? 'bg-red-500' : 'bg-neutral-500'}`}>
                  {incident ? 'Active CBRNE Command' : 'No Active Incident'}
                </span>
                {incident && (
                  <span className="text-seha-100 text-xs font-medium tracking-wide">
                    {incident.refCode} &bull; {incident.type} &bull; {incident.elapsedMinutes ?? 0} minutes
                  </span>
                )}
              </div>
              <h2 className="text-2xl font-bold tracking-tight">Strategic &amp; Operational Command Center</h2>
              <p className="text-seha-100 text-sm mt-1 font-medium">
                One command layer for incident activation, staff mobile app, patient tablet workflow, stakeholder
                engagement and disaster scaling.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button variant="secondary" className="shadow-sm" onClick={() => setModal('sitrep')}>
                Send SITREP
              </Button>
              <Button variant="inverse" onClick={() => setModal('conference')}>
                Start Conference
              </Button>
              <Button variant="inverse" onClick={openLane}>
                Open Decon Lane
              </Button>
            </div>
          </div>
          <div className="absolute right-0 top-0 h-full w-1/3 bg-gradient-to-l from-white/10 to-transparent" />
        </div>
        <p className="text-xs text-neutral-400 -mt-4">
          Decon lane, risk score and stakeholder-ack figures are manual estimates — no decon-lane tracking exists in the backend yet.
        </p>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {commandKpis.map((k) => (
            <KpiCard key={k.label} {...k} />
          ))}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 bg-white rounded-xl border border-neutral-200 shadow-subtle p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-neutral-800">Command Workflow Status</h3>
              <span className="text-xs text-neutral-400 font-medium">CBRNE operations lifecycle</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {workflowStages.map((stage) => (
                <div key={stage.key} className="border border-neutral-200 rounded-xl bg-white p-4">
                  <span
                    className={`w-[30px] h-[30px] rounded-full grid place-items-center text-xs font-bold mb-3 ${STAGE_STYLES[stage.status].badge}`}
                  >
                    {STAGE_NUMBERS[stage.key]}
                  </span>
                  <b className="block text-sm text-neutral-900">{stage.label}</b>
                  <small className="block text-neutral-500 text-[11px] mt-1">{stage.note}</small>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-neutral-200 shadow-subtle p-6">
            <h3 className="font-bold text-neutral-800 mb-4">Command Alerts</h3>
            <div className="space-y-3">
              {alerts.length > 0 ? (
                alerts.map((alert) => (
                  <div key={alert.id} className={`alert-row ${alert.level}`}>
                    {alert.text}
                  </div>
                ))
              ) : (
                <div className="text-sm text-neutral-400 text-center py-6">No alerts</div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Modal
        open={modal === 'sitrep'}
        onClose={() => setModal(null)}
        title="Send SITREP"
        subtitle="Stakeholder and executive situation report."
        confirmLabel="Send SITREP"
        onConfirm={() => {
          setModal(null);
          toast.success('SITREP sent to stakeholders and leadership.');
        }}
      >
        <div className="space-y-5">
          <div>
            <label className="form-label" htmlFor="sitrep-recipients">Recipients</label>
            <select id="sitrep-recipients" className="form-input">
              <option>High Management + DOH + Civil Defense</option>
              <option>All stakeholders</option>
            </select>
          </div>
          <div>
            <label className="form-label" htmlFor="sitrep-message">SITREP Message</label>
            <textarea
              id="sitrep-message"
              className="form-input"
              rows={5}
              defaultValue={sitrepDefault}
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={modal === 'conference'}
        onClose={() => setModal(null)}
        title="Start Command Conference"
        subtitle="Open video/audio bridge for stakeholders and leadership."
        confirmLabel="Start Conference"
        onConfirm={() => {
          setModal(null);
          toast.success('Command conference started.');
        }}
      >
        <div className="space-y-3">
          <div className="alert-row success">Video bridge: Leadership, ED, RSO, Civil Defense, Ambulance</div>
          <div className="alert-row success">Audio fallback: Low bandwidth command bridge</div>
          <div className="alert-row success">Secure chat: Audit-ready event communication room</div>
        </div>
      </Modal>
    </Sidebar>
  );
}
